import { Location } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  throwError,
} from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { FormUtils } from '../../shared/utils/form-utils';
import { StringUtils } from '../../shared/utils/string-utils';
import { FHIRServiceInjectionToken } from '../fhir-service.providers';
import { StandaloneService } from '../standalone.service';
import { buildStandalonePatientSelfContact } from '../standalone-patient-contact';
import { PatientService } from '../../contacts/patient.service';
import { Patient } from '../../contacts/patient.model';
import { ContactsViewModel } from '../../contacts/contacts.view-model';
import { Call } from '../model/call';
import { CallState } from '../model/call-state';
import { CallStatesResponse } from '../model/call-states';
import { ContactInformation } from '../model/contact-information';
import { CallSequence } from '../model/call-sequence';
import { HistoryFilters } from '../model/history-filters';
import { Message } from '../model/message';
import { cycleStatusBadgeVariant, cycleStatusLabel } from '../cycle-status.util';
import { NotificationViewModel } from '../notification.view-model';
import { resolveAlertConfig } from '../../configuration/configuration.defaults';
import { isInsideNotifyWindow } from '../model/alert-cycle.util';
import { alertStartedMessage } from '../utils/alert-started-message';
import { attemptRowLabel, sequenceAttempts } from '../utils/alert-log-attempt.util';
import { MessageBodyDialogComponent } from './message-body-dialog/message-body-dialog.component';
import { CallFiltersComponent } from './call-filters/call-filters.component';
import { CancelButtonComponent } from './cancel-button/cancel-button.component';
import { ContactDialogComponent } from './contact-dialog/contact-dialog.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { subjectLabels, subjectWord } from '../../configuration/subject-label.util';
import {
  AlertCampaignService,
  AlertCampaignSummary,
} from '../../contacts/alert-roster/alert-campaign.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  fhirService = inject(FHIRServiceInjectionToken);
  #patientService = inject(PatientService);
  #contactsViewModel = inject(ContactsViewModel);
  #campaignService = inject(AlertCampaignService);
  campaignById = signal<Map<string, AlertCampaignSummary>>(new Map());
  readonly #location = inject(Location);
  constructor(
    private notificationViewModel: NotificationViewModel,
    private router: Router,
    public route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}
  private static PATIENT_ID_MISSING = 'patientIdMissing';
  private static PROVIDER_ID_MISSING = 'providerIdMissing';
  public DEFAULT_COUNTER_TIMER = 5;

  public patientForm!: FormGroup;
  public providerForm!: FormGroup;
  public messages$!: Observable<Message[] | null>;
  public calls$!: Observable<Call[] | null>;
  public calls!: Call[] | null;
  public messages!: Message[] | null;
  public isOngoing$!: Observable<boolean>;
  public status$!: Observable<CallState>;
  public phoneCallStates$!: Observable<CallStatesResponse>;
  public callingCounter!: number;

  public selectedEmergencyContact$!: Observable<ContactInformation | null>;
  public patientContact!: ContactInformation[];
  public providerContact$!: Observable<ContactInformation | null>;

  loadingEmergencyContacts = toSignal(this.notificationViewModel.loadingPatientContacts$, {
    initialValue: true,
  });
  loadingProviderContacts = toSignal(this.notificationViewModel.loadingProviderContacts$, {
    initialValue: false,
  });
  loadingCallLogs = toSignal(this.notificationViewModel.loadingCallLogs$, {
    initialValue: false,
  });
  loadingMessageLogs = toSignal(this.notificationViewModel.loadingMessageLogs$, {
    initialValue: false,
  });
  loadingCallSequences = toSignal(this.notificationViewModel.loadingCallSequences$, {
    initialValue: false,
  });
  callSequences = toSignal(this.notificationViewModel.callSequences$, {
    initialValue: null as CallSequence[] | null,
  });
  selectedTabIndex = 0;
  expandedSequenceId: string | null = null;

  onAlertLogClosed(sequenceId: string): void {
    // Refresh removes the panel. That close is not the user collapsing the row.
    if (this.loadingCallSequences()) {
      return;
    }
    if (this.expandedSequenceId === sequenceId) {
      this.expandedSequenceId = null;
    }
  }

  public emergencyContacts = toSignal(this.notificationViewModel.patientContacts$, {
    initialValue: [] as ContactInformation[],
  });
  patientSelfContacts = computed(() =>
    this.emergencyContacts().filter((contact) => contact.selfPatient),
  );
  alertContacts = computed(() => {
    const all = this.emergencyContacts();
    return [
      ...all.filter((contact) => contact.selfPatient),
      ...all.filter((contact) => !contact.selfPatient),
    ];
  });
  public providerContacts$: Observable<ContactInformation[]> =
    this.notificationViewModel.providerContacts$;
  public providerContacts: Signal<ContactInformation[]> = toSignal(
    this.notificationViewModel.providerContacts$,
    { initialValue: [] },
  );

  public providerDisabled$ = new BehaviorSubject<boolean>(false);
  public patientDisabled$ = new BehaviorSubject<boolean>(false);

  public userId?: string | null;
  public encounterId?: string | null;

  public currentTab!: 'call' | 'message' | null;

  private destroy$ = new Subject<boolean>();
  public hoverOnNotifyPatient = false;

  @ViewChild(CallFiltersComponent)
  filtersComponent!: CallFiltersComponent;

  public defaultMessages?: Message[] | null;
  public defaultCalls?: Call[] | null;

  public filtersApplied = false;
  public isStandaloneMode = false;
  public defaultLocationId?: string;
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  async ngOnInit(): Promise<void> {
    this.#openPatientFromRoute();
    if (!this.currentOrganization()) {
      this.#contactsViewModel.loadProfile();
    }
    this.isStandaloneMode = this.fhirService instanceof StandaloneService;
    this.notificationViewModel.setLoadingProviderContacts(true);
    this.callingCounter = this.DEFAULT_COUNTER_TIMER;
    this.messages$ = this.notificationViewModel.messages$;
    this.calls$ = this.notificationViewModel.calls$;
    this.status$ = this.notificationViewModel.callState$;
    this.isOngoing$ = this.notificationViewModel.isOngoing$();
    this.phoneCallStates$ = this.notificationViewModel.phoneNumberCallStates$;

    this.selectedEmergencyContact$ = this.notificationViewModel.selectedPatientContact$;
    this.providerContact$ = this.notificationViewModel.selectedProviderContact$;

    this.patientContact = await this.fhirService.getPatientContact();
    const patientContacts: ContactInformation[] =
      await this.fhirService.getPatientEmergencyContactsInformation();

    this.userId = await this.fhirService.getUserId();
    this.refreshPatientContacts(patientContacts);

    this.calls$.subscribe((calls) => {
      this.defaultCalls = calls;
      this.calls = calls;
    });

    this.messages$.subscribe((messages) => {
      this.defaultMessages = messages;
      this.messages = messages;
    });

    this.notificationViewModel.callSequences$
      .pipe(
        takeUntil(this.destroy$),
        filter((sequences): sequences is CallSequence[] => !!sequences?.length),
      )
      .subscribe((sequences) => {
        const sequenceId = this.route.snapshot.queryParams['sequenceId'];
        if (sequenceId && sequences.some((sequence) => sequence.id === sequenceId)) {
          this.expandedSequenceId = sequenceId;
        }
        this.loadCampaignsForSequences(sequences);
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: any) => {
        const patientId = params['patientId'];
        const sequenceId = params['sequenceId'];
        if (sequenceId) {
          this.selectedTabIndex = 1;
          this.expandedSequenceId = sequenceId;
        }
        if (patientId) {
          this.notificationViewModel
            .refreshCallSequences(patientId)
            .pipe(take(1))
            .subscribe(() => {
              if (sequenceId) {
                this.expandedSequenceId = sequenceId;
              }
            });
        }
      });

    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        map((params: any) => params['patientId']),
      )
      .subscribe((patientId: string) => {
        this.notificationViewModel
          .refreshPhoneNumberCallStates(patientId)
          .pipe(take(1))
          .subscribe();
      });

    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['fhirAuthorizeError']),
      )
      .subscribe((fhirAuthorizeError: any) => {
        if (fhirAuthorizeError === 'true') {
          this.snackBar.open(
            "Sorry, we couldn't load the information from EPIC. Please try again later, or enter it manually.",
            'Okay',
          );
        }
      });

    let providerContacts: ContactInformation[] = [];

    if (this.isStandaloneMode) {
      await this.loadStandaloneCallbackLocations();
    } else {
      this.route.queryParams
        .pipe(
          takeUntil(this.destroy$),
          map((params: any) => params['providerId']),
        )
        .subscribe(async (providerId: string) => {
          providerContacts = await this.fhirService.getPractitionerContactsInformation(providerId);
          this.refreshProviderContacts(providerContacts);
        });
    }
  }

  /** Standalone patient load reads sessionStorage, not the route. */
  #openPatientFromRoute(): void {
    const routePatientId = this.route.snapshot.queryParams['patientId'];
    const sequenceId = this.route.snapshot.queryParams['sequenceId'];
    if (sequenceId) {
      this.selectedTabIndex = 1;
      this.expandedSequenceId = sequenceId;
    }
    if (!routePatientId || sessionStorage.getItem('patientId') === routePatientId) {
      return;
    }
    sessionStorage.setItem('patientId', routePatientId);
    this.notificationViewModel.resetState();
    if (this.fhirService instanceof StandaloneService) {
      this.fhirService.clearPatientCache();
    }
  }

  private async loadStandaloneCallbackLocations(): Promise<void> {
    const patientId =
      this.route.snapshot.queryParams['patientId'] ?? sessionStorage.getItem('patientId');

    if (!patientId) {
      this.notificationViewModel.setLoadingProviderContacts(false);
      return;
    }

    try {
      const [locationsResponse, patient] = await Promise.all([
        firstValueFrom(this.#patientService.getLocations()),
        firstValueFrom(this.#patientService.getPatient(patientId)),
      ]);

      this.defaultLocationId = patient.locationId;
      await this.notificationViewModel.loadCallbackLocations(
        locationsResponse.result ?? [],
        patient.locationId,
      );
    } catch (err) {
      console.error('Error loading callback locations', err);
      this.notificationViewModel.setLoadingProviderContacts(false);
    }
  }

  public isDefaultCallbackLocation(contact: ContactInformation): boolean {
    const locationId = contact.locationId ?? contact.entityId;
    return !!this.defaultLocationId && locationId === this.defaultLocationId;
  }

  public trackCallbackLocation(contact: ContactInformation): string {
    return contact.locationId || contact.entityId;
  }

  public compareCallbackLocations(
    a: ContactInformation | null,
    b: ContactInformation | null,
  ): boolean {
    if (!a || !b) {
      return a === b;
    }
    return (
      (a.locationId || a.entityId) === (b.locationId || b.entityId)
    );
  }

  subjectSingular(): string {
    return subjectLabels(this.currentOrganization()).singular;
  }

  subjectWord(): string {
    return subjectWord(this.subjectSingular());
  }

  alertSubjectContact(): ContactInformation {
    const existing = this.patientSelfContacts()[0] ?? this.patientContact?.[0];
    if (existing) {
      return existing;
    }

    const patientId =
      this.route.snapshot.queryParams['patientId'] ?? sessionStorage.getItem('patientId') ?? '';
    const name = this.route.snapshot.queryParams['patientName'] ?? '';
    return {
      entityId: patientId,
      name,
      emergencyContactName: name,
      contactNumber: '',
      createdOn: Date.now(),
      isPatient: true,
      selfPatient: true,
    };
  }

  public openEditPatientDialog(contact: ContactInformation): void {
    const patientId =
      this.route.snapshot.queryParams['patientId'] ?? sessionStorage.getItem('patientId');

    combineLatest([
      this.#patientService.getLocations(),
      patientId ? this.#patientService.getPatient(patientId) : of(undefined),
    ])
      .pipe(take(1))
      .subscribe(([locationsResponse, patient]) => {
        this.dialog
          .open(ContactDialogComponent, {
            autoFocus: true,
            data: {
              contact: { ...contact, isPatient: true },
              userId: this.userId,
              editPatientTitle: true,
              subjectLabel: this.subjectSingular(),
              callbackLocations: locationsResponse.result ?? [],
              defaultLocationId: patient?.locationId,
            },
          })
          .afterClosed()
          .subscribe((result) => {
            if (!result) {
              return;
            }

            if (!patientId) {
              this.snackBar.open(
                `Could not save ${this.subjectWord()}. ${this.subjectSingular()} id is missing.`,
                'Okay',
                {
                duration: 5000,
              });
              return;
            }

            const name = (result.name ?? result.emergencyContactName ?? '').trim();
            const phone = result.contactNumber ?? '';
            const locationId = result.locationId;

            this.#patientService
              .updatePatient(patientId, {
                name,
                phone,
                locationId,
              })
              .pipe(take(1))
              .subscribe({
                next: (updated) => {
                  if (this.fhirService instanceof StandaloneService) {
                    this.fhirService.clearPatientCache();
                  }

                  this.defaultLocationId = updated.locationId;

                  const selfContact = buildStandalonePatientSelfContact(updated);
                  if (selfContact) {
                    this.patientContact = [selfContact];
                    this.refreshPatientContacts([selfContact]);
                  }

                  if (this.isStandaloneMode) {
                    this.loadStandaloneCallbackLocations();
                  }

                  this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { patientName: updated.name },
                    queryParamsHandling: 'merge',
                  });

                  this.snackBar.open(`${this.subjectSingular()} saved.`, 'Okay', { duration: 5000 });
                },
                error: (err) => {
                  console.error('Error updating patient', err);
                  this.snackBar.open(`Could not save ${this.subjectWord()}. Please try again.`, 'Okay', {
                    duration: 5000,
                  });
                },
              });
          });
      });
  }

  public archivePatient(contact: ContactInformation): void {
    if (!this.isStandaloneMode) {
      return;
    }

    const patientId =
      this.route.snapshot.queryParams['patientId'] ?? sessionStorage.getItem('patientId');

    if (!patientId) {
      this.snackBar.open(
        `Could not archive ${this.subjectWord()}. ${this.subjectSingular()} id is missing.`,
        'Okay',
        {
        duration: 5000,
      });
      return;
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `Archive ${this.subjectSingular()}?`,
          message: `This ${this.subjectWord()} will be hidden from the Contacts list. Call and message history will still be available at the organization level. You can restore them later by adding the same name and phone.`,
          yesText: 'Archive',
          noText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() => this.#patientService.getPatient(patientId)),
        switchMap((patient) =>
          this.#patientService.updatePatient(patientId, {
            name: patient.name,
            phone: patient.phone,
            locationId: patient.locationId,
            archived: true,
          }),
        ),
        take(1),
      )
      .subscribe({
        next: () => {
          if (this.fhirService instanceof StandaloneService) {
            this.fhirService.clearPatientCache();
          }
          // Contacts list is cached in the root view-model and skips reload when already loaded.
          this.#contactsViewModel.removePatient(patientId);
          this.snackBar.open(`${this.subjectSingular()} archived.`, 'Okay', { duration: 5000 });
          this.router.navigate(['/contacts']);
        },
        error: (err) => {
          console.error('Error archiving patient', err);
          this.snackBar.open(`Could not archive ${this.subjectWord()}. Please try again.`, 'Okay', {
            duration: 5000,
          });
        },
      });
  }

  showOngoingCallInfoForPatient() {
    if (!this.patientContact[0]?.contactNumber) return;
    this.phoneCallStates$.pipe(first()).subscribe((state) => {
      const endTime = state[this.patientContact[0]?.contactNumber].activeUntil;
      if (!endTime) return;

      let message = '';

      const now = Date.now();
      if (endTime > now) {
        const config = resolveAlertConfig(this.currentOrganization()?.alertConfig);
        message = alertStartedMessage(config);
      } else {
        message = `Please refresh the page to make the call.`;
      }

      this.snackBar.open(message, 'Okay');
    });
  }

  public onSelectedTabIndex(index: number): void {
    this.selectedTabIndex = index;
    if (index === 1) {
      this.refreshAlertLog();
    }
  }

  public refreshAlertLog(): void {
    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['patientId']),
        mergeMap((id: string) => this.notificationViewModel.refreshCallSequences(id)),
      )
      .subscribe();
  }

  public cycleLabel(sequence: CallSequence): string {
    return cycleStatusLabel(sequence.cycleState);
  }

  public cycleBadge(sequence: CallSequence): string {
    return cycleStatusBadgeVariant(sequence.cycleState);
  }

  public formatCycleDate(epochMs: number): string {
    return new Date(epochMs).toLocaleString();
  }

  sequenceAttempts = sequenceAttempts;
  attemptRowLabel = attemptRowLabel;

  showMessageBody(body: string): void {
    this.dialog.open(MessageBodyDialogComponent, {
      data: body,
      width: '420px',
    });
  }

  sequenceContactLine(sequence: CallSequence): string {
    const phone = sequence.to ? this.formatContactNumber(sequence.to) : '';
    const name = this.contactNameForPhone(sequence.to, sequence.callType === 'patient');
    if (name && phone) {
      return `${name} ${phone}`;
    }
    return name || phone || 'Unknown';
  }

  sequenceTypeLabel(sequence: CallSequence): string {
    return sequence.callType === 'patient' ? this.subjectSingular() : 'Contact';
  }

  sequenceCampaignTitle(sequence: CallSequence): string {
    const campaignId = sequence.alertCampaignId?.trim();
    if (!campaignId) {
      return 'N/A';
    }
    return this.campaignById().get(campaignId)?.title?.trim() || 'N/A';
  }

  sequenceCampaignDate(sequence: CallSequence): string {
    const campaignId = sequence.alertCampaignId?.trim();
    if (!campaignId) {
      return 'N/A';
    }
    const createdDate = this.campaignById().get(campaignId)?.createdDate;
    return createdDate ? this.formatCycleDate(createdDate) : 'N/A';
  }

  sequenceCampaignDescription(sequence: CallSequence): string {
    const campaignId = sequence.alertCampaignId?.trim();
    if (!campaignId) {
      return 'N/A';
    }
    return this.campaignById().get(campaignId)?.description?.trim() || 'N/A';
  }

  private loadCampaignsForSequences(sequences: CallSequence[]): void {
    const ids = [
      ...new Set(
        sequences
          .map((sequence) => sequence.alertCampaignId?.trim())
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    if (!ids.length) {
      this.campaignById.set(new Map());
      return;
    }
    forkJoin(
      ids.map((id) =>
        this.#campaignService.getCampaign(id).pipe(
          catchError(() => of(null)),
          map((campaign) => ({ id, campaign })),
        ),
      ),
    )
      .pipe(take(1))
      .subscribe((results) => {
        const next = new Map<string, AlertCampaignSummary>();
        for (const entry of results) {
          if (entry.campaign) {
            next.set(entry.id, entry.campaign);
          }
        }
        this.campaignById.set(next);
      });
  }

  sequenceCallbackName(sequence: CallSequence): string {
    const providerId = sequence.providerId;
    if (!providerId) {
      return 'Unknown';
    }
    const match = this.providerContacts().find((contact) => {
      const locationId = contact.locationId || contact.entityId;
      return locationId === providerId || locationId === providerId.replace(/^#LOCATION#/, '');
    });
    return match?.name?.trim() || 'Unknown';
  }

  sequencePatientName(): string {
    return this.route.snapshot.queryParams['patientName'] || 'Unknown';
  }

  private contactNameForPhone(phone: string | undefined, isSubject: boolean): string {
    if (isSubject) {
      return this.sequencePatientName();
    }
    if (!phone) {
      return '';
    }
    const match = this.emergencyContacts().find(
      (contact) => this.cleanPhoneNumber(contact.contactNumber) === this.cleanPhoneNumber(phone),
    );
    return match?.emergencyContactName?.trim() || match?.name?.trim() || '';
  }

  public selectFilters(filters: HistoryFilters) {
    let filtersEmpty = true;

    if (filters.type === 'call') {
      let filteredCalls = this.defaultCalls;

      if (filters.nameOrNumber) {
        filteredCalls = this.filterCallsByNumberOrName(filteredCalls || [], filters.nameOrNumber);
        filtersEmpty = false;
      }

      if (filters.duration?.min || filters.duration?.max) {
        filteredCalls = this.filterCallByDuration(filteredCalls || [], filters.duration);
        filtersEmpty = false;
      }

      if (filters && filters.status && filters.status.length > 0) {
        filteredCalls = this.filterCallByStatus(filteredCalls || [], filters.status);
        filtersEmpty = false;
      }

      if (filters && filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) {
        filteredCalls = this.filterCallByDateRange(filteredCalls || [], filters.dateRange);
        filtersEmpty = false;
      }

      this.calls = filteredCalls || [];
    } else if (filters.type === 'message') {
      let filteredMessages = this.defaultMessages;

      if (filters.nameOrNumber) {
        filteredMessages = this.filterMessagesByNumberOrName(
          filteredMessages || [],
          filters.nameOrNumber,
        );
        filtersEmpty = false;
      }

      if (filters && filters.status && filters.status.length > 0) {
        filteredMessages = this.filterMessageByStatus(filteredMessages || [], filters.status);
        filtersEmpty = false;
      }

      if (filters && filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) {
        filteredMessages = this.filterMessagesByDateRange(
          filteredMessages || [],
          filters.dateRange,
        );
        filtersEmpty = false;
      }

      this.messages = filteredMessages || [];
    }

    if (filtersEmpty) {
      this.filtersApplied = false;
    } else {
      this.filtersApplied = true;
    }
  }

  filterCallsByNumberOrName(calls: Call[], data: string) {
    data = data.toLowerCase();
    return calls.filter((call) => {
      return (
        this.cleanPhoneNumber(call.to).includes(data) ||
        this.cleanPhoneNumber(call.toFormatted).includes(data) ||
        this.findContactNameFromCallOrMessage(call).toLowerCase().includes(data)
      );
    });
  }

  filterMessagesByNumberOrName(messages: Message[], data: string) {
    data = data.toLowerCase();
    return messages.filter((message) => {
      return (
        this.cleanPhoneNumber(message.to).includes(data) ||
        this.findContactNameFromCallOrMessage(message).toLowerCase().includes(data)
      );
    });
  }

  filterCallByDuration(calls: Call[], duration: { min: string | null; max: string | null }) {
    if (duration && duration.min && duration.max) {
      return calls.filter((call) => {
        if (
          call &&
          call.duration &&
          call.status === 'completed' &&
          (call.answeredBy === 'human' || call.answeredBy === 'unknown')
        ) {
          return (
            Number(this.cleanPhoneNumber(this.transformToMinutesSeconds(call.duration))) >=
              Number(this.cleanPhoneNumber(duration.min!)) &&
            Number(this.cleanPhoneNumber(this.transformToMinutesSeconds(call.duration))) <=
              Number(this.cleanPhoneNumber(duration.max!))
          );
        }
        return false;
      });
    }
    if (duration.min) {
      return calls.filter((call) => {
        if (
          call &&
          call.duration &&
          call.status === 'completed' &&
          (call.answeredBy === 'human' || call.answeredBy === 'unknown')
        ) {
          return (
            Number(this.cleanPhoneNumber(this.transformToMinutesSeconds(call.duration))) >=
            Number(this.cleanPhoneNumber(duration.min!))
          );
        }
        return false;
      });
    }
    if (duration.max) {
      return calls.filter((call) => {
        if (
          call &&
          call.duration &&
          call.status === 'completed' &&
          (call.answeredBy === 'human' || call.answeredBy === 'unknown')
        ) {
          return (
            Number(this.cleanPhoneNumber(this.transformToMinutesSeconds(call.duration))) <=
            Number(this.cleanPhoneNumber(duration.max!))
          );
        }
        return false;
      });
    }
    return;
  }

  filterCallByStatus(calls: Call[], statusArray: string[]) {
    const filteredResults: Call[] = [];

    statusArray.forEach((status) => {
      calls.forEach((call) => {
        if (
          status === 'successful' &&
          call.status === 'completed' &&
          (call.answeredBy === 'human' || call.answeredBy === 'unknown')
        ) {
          filteredResults.push(call);
        }
        if (
          status === 'missed' &&
          call.status === 'completed' &&
          call.answeredBy !== 'human' &&
          call.answeredBy !== 'unknown'
        ) {
          filteredResults.push(call);
        }
        if (
          status === 'initiated' &&
          (call.status === 'queued' ||
            call.status === 'ringing' ||
            call.status === 'initiated' ||
            call.status === 'in_progress')
        ) {
          filteredResults.push(call);
        }
        if (status === 'error' && call.status === 'failed') {
          filteredResults.push(call);
        }
      });
    });

    return filteredResults;
  }

  filterMessageByStatus(messages: Message[], statusArray: string[]) {
    const filteredResults: Message[] = [];

    statusArray.forEach((status) => {
      messages.forEach((message) => {
        if (
          (status === 'undelivered' && message.status === 'undelivered') ||
          message.status === 'failed'
        ) {
          filteredResults.push(message);
        }
        if (
          (status === 'delivered' && message.status === 'delivered') ||
          message.status === 'sent'
        ) {
          filteredResults.push(message);
        }
      });
    });

    return filteredResults;
  }

  resetFilters() {
    if (this.filtersComponent) {
      this.filtersComponent.reset();
      this.filtersComponent.apply();
      this.filtersComponent.close();
    }
  }

  filterCallByDateRange(calls: Call[], dateRange: { start: Date | null; end: Date | null }) {
    return calls.filter((call) => {
      const start = dateRange.start;
      const end = dateRange.end;

      const created = new Date(call.created);
      if (!start || !end) return false;

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return created >= start && created <= end;
    });
  }

  filterMessagesByDateRange(
    messages: Message[],
    dateRange: { start: Date | null; end: Date | null },
  ) {
    return messages.filter((message) => {
      const start = dateRange.start;
      const end = dateRange.end;
      const created = new Date(Number(message.dateSent));

      if (!start || !end) return false;

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return created >= start && created <= end;
    });
  }

  public openContactDialog(contact: any | null, isPatient: boolean): void {
    contact = contact ? (contact as ContactInformation) : null;
    this.dialog
      .open(ContactDialogComponent, {
        autoFocus: true,
        data: { contact: contact ? { ...contact, isPatient } : { isPatient }, userId: this.userId },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (isPatient) {
            this.bindToPatientForm(result);
            this.addPatientContact();
          } else {
            delete result.isPatient;
            delete result.emergencyContactName;
            this.bindToProviderForm(result);
            this.addProviderContact();
          }
        }
      });
  }

  private bindToPatientForm(contact: ContactInformation): void {
    this.route.queryParams.pipe(take(1)).subscribe((params: any) => {
      const patientData = {
        ...contact,
        name: params['patientName'],
        entityId: params['patientId'],
        contactNumber: StringUtils.stripNonDigits(contact.contactNumber),
        createdOn: Date.now(),
        emergencyContactName: contact.emergencyContactName,
      };

      this.notificationViewModel.editPatientContact(patientData);
    });
  }

  private bindToProviderForm(contact: ContactInformation): void {
    this.route.queryParams.pipe(take(1)).subscribe((params: any) => {
      const providerData = {
        ...contact,
        entityId: params['providerId'],
        contactNumber: StringUtils.stripNonDigits(contact.contactNumber),
        createdOn: Date.now(),
      };

      this.notificationViewModel.editProviderContact(providerData);
    });
  }

  public addPatientContact(): void {
    this.patientDisabled$.next(true);

    this.notificationViewModel
      .savePatientContact()
      .pipe(take(1))
      .subscribe(() => this.handlePatientSuccess());
  }

  public addProviderContact(): void {
    this.providerDisabled$.next(true);
    this.notificationViewModel
      .saveProviderContact()
      .pipe(take(1))
      .subscribe(() => this.handleProviderSuccess());
  }

  private handleProviderSuccess() {
    // FormUtils.resetAndClean(this.providerForm);
    this.providerDisabled$.next(false);
  }

  private handlePatientSuccess(): void {
    // FormUtils.resetAndClean(this.patientForm);
    this.patientDisabled$.next(false);
  }

  public onProviderSelectionChange(contact: ContactInformation): void {
    this.notificationViewModel.selectProviderContact(contact);
  }

  public onPatientSelectionChange(contact: ContactInformation): void {
    this.notificationViewModel.selectPatientContact(contact);
  }

  public deleteProviderContact(contact: ContactInformation, event: MouseEvent): void {
    event && event.stopImmediatePropagation();
    this.deleteContact(contact, false);
  }

  public deletePatientContact(contact: ContactInformation, event: MouseEvent): void {
    event && event.stopImmediatePropagation();
    this.deleteContact(contact, true);
  }

  public deleteContact(contact: unknown, isPatient: boolean): void {
    this.dialog
      .open(ConfirmationDialogComponent)
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        switchMap(() =>
          isPatient
            ? this.notificationViewModel.deletePatientContact(contact as ContactInformation)
            : this.notificationViewModel.deleteProviderContact(contact as ContactInformation),
        ),
        take(1),
      )
      .subscribe(() => {});
  }

  public formatContactNumber(num: string): string {
    let formatted = num.substring(2);

    if (formatted.charAt(0) === '1') {
      formatted = formatted.substring(1);
    }

    return FormUtils.formatPhoneNumber(formatted);
  }

  public refreshCallLog(): void {
    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['patientId']),
        mergeMap((id: string) => this.notificationViewModel.refreshCallLogs(id)),
      )
      .subscribe();
  }

  public refreshMessages(): void {
    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['patientId']),
        mergeMap((id: string) => this.notificationViewModel.refreshMessageLogs(id)),
      )
      .subscribe();
  }

  public refreshPatientContacts(fhirContacts: ContactInformation[]): void {
    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['patientId']),
        map((patientId: any) =>
          patientId
            ? patientId
            : throwError(() => new Error(DashboardComponent.PROVIDER_ID_MISSING)),
        ),
        filter((providerId) => typeof providerId === 'string'),
        mergeMap((patientId: any) =>
          this.notificationViewModel.refreshPatientContacts(patientId, fhirContacts),
        ),
      )
      .subscribe(() => {});
  }

  public refreshProviderContacts(fhirContacts: ContactInformation[]): void {
    this.route.queryParams
      .pipe(
        take(1),
        map((params: any) => params['providerId']),
        map((providerId: any) =>
          providerId
            ? providerId
            : throwError(() => new Error(DashboardComponent.PROVIDER_ID_MISSING)),
        ),
        filter((providerId) => {
          if (typeof providerId === 'string') {
            return true;
          }
          this.notificationViewModel.markCallbackLocationsUnavailable();
          return false;
        }),
        mergeMap((providerId: string) => {
          return this.notificationViewModel.refreshProviderContacts(providerId, fhirContacts);
        }),
      )
      .subscribe(() => {});
  }

  public formatTo(message: Message): string {
    const to = StringUtils.stripNonDigits(message.to);
    return FormUtils.formatPhoneNumber(to.charAt(0) === '1' ? to.substring(1) : to);
  }

  public cancelNotify(contact?: ContactInformation): void {
    const patientId = this.route.snapshot.queryParams['patientId'];
    const resolvedContact = contact ?? this.patientContact?.[0];

    if (!patientId || !resolvedContact?.contactNumber) {
      this.notificationViewModel.resetNotification();
      return;
    }

    this.phoneCallStates$.pipe(first()).subscribe((states) => {
      if (states[resolvedContact.contactNumber]?.state === 'inProgress') {
        this.notificationViewModel
          .cancelAlert(patientId, resolvedContact.contactNumber)
          .pipe(take(1))
          .subscribe((result) => {
            if (result.cancelledCount > 0) {
              this.snackBar.open(
                'This alert has been stopped. Scheduled calls and texts will stop. ' +
                  'A call already handed off to the phone network cannot be cancelled and may still complete.',
                'Okay',
              );
            } else {
              this.snackBar.open('No active alert found for this contact.', 'Okay');
            }
          });
      } else {
        this.notificationViewModel.resetNotification();
      }
    });
  }

  openAlertLogRecord(sequenceId: string): void {
    this.selectedTabIndex = 1;
    this.expandedSequenceId = sequenceId;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sequenceId },
      queryParamsHandling: 'merge',
    });
  }

  public notify(): void {
    const patientId = this.route.snapshot.queryParams['patientId'];

    if (!patientId) return;

    const config = resolveAlertConfig(this.currentOrganization()?.alertConfig);
    if (!isInsideNotifyWindow(Date.now(), config)) {
      this.snackBar.open(
        'Alerts can only be started during your organization notify hours.',
        'Okay',
        { duration: 8000 },
      );
      return;
    }

    // Immediate feedback while notify + call-state refresh run (SAM/local can be slow).
    this.snackBar.open('Sending alert…', undefined, { duration: undefined });

    this.notificationViewModel
      .notify(patientId)
      .pipe(take(1))
      .subscribe({
        next: (sequence) => {
          this.handleNotificationSuccess(sequence);
        },
        error: () => {
          this.snackBar.open('Could not send the alert. Please try again.', 'Okay', {
            duration: 5000,
          });
          this.notificationViewModel.resetNotification();
        },
      });
  }

  findContactNameFromCallOrMessage(input: Call | Message): string {
    const phoneNumber = input.to;

    if (input.selfPatient) {
      const patientName = this.route.snapshot.queryParams['patientName'];
      return patientName;
    }

    if (this.emergencyContacts().length === 0) {
      return '';
    }

    const patientContact = this.emergencyContacts().filter(
      (contact) =>
        this.cleanPhoneNumber(contact.contactNumber) === this.cleanPhoneNumber(phoneNumber),
    );
    return patientContact.length > 0 && patientContact[0].emergencyContactName
      ? patientContact[0].emergencyContactName
      : 'Contact not found';
  }

  cleanPhoneNumber(phone: string): string {
    return phone.replace(/[^a-zA-Z0-9 ]/g, '');
  }

  notifyPatient() {
    if (this.patientContact.length === 0) {
      return;
    }

    const falseContact = this.patientContact[0];
    falseContact.selfPatient = true;

    this.onPatientSelectionChange(falseContact);

    const cancelButtonSnackbar = this.snackBar.openFromComponent(CancelButtonComponent, {
      duration: 5000,
      panelClass: ['cancel-snackbar'],
    });

    // Listen to the cancellation
    cancelButtonSnackbar.instance.actionCancelled.subscribe(() => {
      this.cancelNotify(falseContact);
    });

    // Listen to the timeout
    cancelButtonSnackbar.instance.actionTimeout.subscribe(() => {
      this.notify();
    });
  }

  public isProviderSelected(): boolean {
    return this.notificationViewModel.isProviderSelected();
  }

  public isPatientSelected(): boolean {
    return this.notificationViewModel.isPatientSelected();
  }

  private handleNotificationSuccess(sequence?: CallSequence): void {
    const config = resolveAlertConfig(this.currentOrganization()?.alertConfig);
    this.snackBar.open(alertStartedMessage(config), 'Okay');
  }

  public transformToMinutesSeconds(value: string | number): string {
    const numericValue = typeof value === 'string' ? +value : value;

    if (isNaN(numericValue) || numericValue === 0) {
      return '0:00';
    }

    const minutes = Math.floor(numericValue / 60);
    const seconds = numericValue - minutes * 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toFixed(0).padStart(2, '0')}`;
  }

  private handleAuthorizationError(): void {
    this.snackBar.open(
      'It appears as if your subscription has expired.  Please contact us at jtsi.net ' +
        'to discuss renewing your contract before using AFAS.  Thank you.',
      'Okay',
    );
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onBack() {
    this.#location.back();
  }
}
